import { StorageService } from '@/shared/utils/storage-service'
import { Injector } from '@/config/dependency-injection'
import { StringMap } from '@/shared/utils/map'
import { decodeJwt } from '@/shared/utils/decode-jwt'
import { fromUnixTime, isAfter } from 'date-fns'
import { loggerFor } from '@/shared/utils/logger'

export class TokenHolder {
  private readonly storage: StorageService = Injector.get(StorageService)

  private logger = loggerFor(`TokenHolder: ${this.storageKey}`)

  constructor(private readonly storageKey: string = 'token') {
    this.ensureTokenIsValid()
  }

  public clear(): void {
    this.storage.removeItem(this.storageKey)
  }

  /**
   * Check if a token is available and if it is still valid. If it is not then this method will
   * clear the storage and return false.
   */
  public ensureTokenIsValid(): boolean {
    let valid = false

    this.logger.debug('Checking if token is available and has not expired')

    const token = this.get()

    if (token !== undefined && this.isValid(token) === false) {
      this.logger.debug('Token has expired, clearing storage')

      this.clear()
    } else if (token !== undefined) {
      this.logger.debug('Token has not yet expired')

      valid = true
    } else {
      this.logger.debug('No token available')
    }

    return valid
  }

  public get(): string | undefined {
    const token = this.storage.getItem<string>(this.storageKey)

    return token ? token : undefined
  }

  public hasToken() {
    return this.get() !== undefined
  }

  /**
   * Only called if a token is present
   * @param f
   */
  public ifPresent(f: (token: string) => void) {
    const token = this.get()

    if (token !== undefined && this.isValid(token)) {
      f(token)
    } else {
      this.clear()
    }

    return this
  }

  public set(token: string): void {
    this.logger.debug('Saving token to storage')

    this.storage.setItem(this.storageKey, token)
  }

  private isValid(token: string): boolean {
    let valid: boolean = false

    // Check if the token is valid and if so if it has not expired
    const jwt: StringMap<any> = decodeJwt(token)

    if (jwt != null) {
      const validUntil = fromUnixTime(jwt['exp'] as number)

      this.logger.debug(`Token is valid until ${validUntil}`)

      if (isAfter(validUntil, Date.now())) {
        valid = true
      }

      this.logger.debug(`Token is ${valid ? 'valid' : 'invalid'}`)
    }

    return valid
  }
}
